// src/pages/Dashboard/exportReport.js
// Report export for the admin dashboard.
//  • PDF  — a picture-perfect copy of the statistical view, the graphical view, or both
//           (uses jspdf + html2canvas, already in package.json; loaded only when exporting)
//  • CSV  — every number on the dashboard as tables (opens directly in Excel)
// Everything is built from the same live data the dashboard shows, so the report always
// matches the screen, including any filters that are applied.

const LABELS = { whatsapp: 'WhatsApp', sms: 'SMS' };
const humanize = (key) =>
  LABELS[key] ||
  String(key == null || key === '' ? 'unknown' : key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const pad2 = (n) => String(n).padStart(2, '0');
export const fileStamp = (d = new Date()) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}_${pad2(d.getHours())}${pad2(d.getMinutes())}`;

const fmtDate = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

/** Human-readable summary of the active filters (used in both CSV and PDF). */
export function describeFilters(filters = {}, data) {
  const parts = [];
  if (filters.from_date || filters.to_date) {
    parts.push(`Applied between ${filters.from_date ? fmtDate(filters.from_date) : 'start'} and ${filters.to_date ? fmtDate(filters.to_date) : 'today'}`);
  }
  if (filters.department) parts.push(`Department: ${filters.department}`);
  if (filters.requirement_status) parts.push(`Requirement status: ${humanize(filters.requirement_status)}`);
  if (filters.requirement_id) {
    const r = (data?.filter_options?.requirements || []).find((x) => String(x.id) === String(filters.requirement_id));
    parts.push(`Requirement: ${r ? `${r.requirement_code} - ${r.title}` : filters.requirement_id}`);
  }
  return parts.length ? parts.join(' | ') : 'No filters (all data)';
}

// ───────────────────────────── CSV ─────────────────────────────

const cell = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * Build one CSV file with a section per dashboard card.
 * opts.statuses      — application status columns to include (same as on screen)
 * opts.showVacancies — include vacancy columns only when vacancies are in use
 */
export function buildCsv(data, filters, { statuses = [], showVacancies = false } = {}) {
  const rows = [];
  const line = (...cols) => rows.push(cols.map(cell).join(','));
  const blank = () => rows.push('');
  const section = (title) => {
    blank();
    line(`## ${title}`);
  };
  const k = data.kpis || {};

  line('JMK Recruitment Dashboard Report');
  line('Generated', new Date(data.generated_at || Date.now()).toLocaleString('en-IN'));
  line('Filters', describeFilters(filters, data));

  section('Summary');
  line('Metric', 'Value', 'Note');
  line('Requirements', k.requirements_total, `${k.requirements_open} open`);
  if (showVacancies) {
    line('Vacancies (total)', k.vacancies_total);
    line('Vacancies remaining', k.vacancies_remaining);
  }
  line('Applications', k.applications_total);
  line('Candidates registered', k.candidates_total, `+${k.candidates_last_30_days} in last 30 days (not filtered)`);
  line('In training', k.in_training);
  line('Joined', k.joined);
  line('Open tickets', k.tickets_open, 'open + in progress (not filtered)');

  section('Hiring pipeline');
  line('Status', 'Applications', '% of total');
  const total = k.applications_total || 0;
  (data.pipeline || [])
    .filter((p) => statuses.includes(p.key) || p.count > 0)
    .forEach((p) => line(humanize(p.key), p.count, total ? `${Math.round((p.count / total) * 100)}%` : '0%'));

  section('Requirements');
  line(
    'Code', 'Title', 'Department', 'Job type', 'Status', 'Locations',
    ...(showVacancies ? ['Vacancies', 'Joined', 'Remaining'] : []),
    'Applications', ...statuses.map(humanize), 'Created'
  );
  (data.requirements || []).forEach((r) => {
    line(
      r.requirement_code, r.title, r.department || '', humanize(r.job_type || ''), humanize(r.status),
      (r.locations || []).map((l) => l.name).join('; '),
      ...(showVacancies ? [r.vacancies ?? '', r.filled ?? 0, r.remaining_vacancy ?? ''] : []),
      r.total_applications, ...statuses.map((s) => (r.status_counts || {})[s] || 0), fmtDate(r.created_at)
    );
  });

  section('Requirement locations');
  line('Requirement code', 'Requirement', 'Location', 'Open for applications',
    ...(showVacancies ? ['Vacancies', 'Remaining'] : []), 'Applications', 'Training', 'Joined');
  (data.requirements || []).forEach((r) =>
    (r.locations || []).forEach((l) =>
      line(r.requirement_code, r.title, l.name, l.is_open ? 'Yes' : 'No',
        ...(showVacancies ? [l.vacancy_count ?? '', l.remaining_vacancy ?? ''] : []),
        l.applications_count, l.training_count, l.joined_count)
    )
  );

  section('Applications by department');
  line('Department', 'Requirements', 'Open requirements', 'Applications', 'Joined');
  (data.departments || []).forEach((d) => line(d.name, d.requirements, d.open, d.applications, d.joined));

  section('Applications by location');
  line('Location', 'Requirements', ...(showVacancies ? ['Vacancies'] : []), 'Applications', 'Training', 'Joined');
  (data.locations || []).forEach((l) =>
    line(l.name, l.requirements, ...(showVacancies ? [l.vacancies || ''] : []), l.applications, l.training, l.joined)
  );

  section('Application source');
  line('Source', 'Applications');
  (data.sources || []).forEach((s) => line(humanize(s.key), s.count));

  const trend = data.trend || {};
  const srcs = trend.sources || [];
  section(`Applications over time (${trend.granularity === 'day' ? 'daily' : 'monthly'})`);
  line(trend.granularity === 'day' ? 'Date' : 'Month', 'Total', ...srcs.map(humanize));
  (trend.points || []).forEach((p) => line(p.period, p.total, ...srcs.map((s) => (p.by_source || {})[s] || 0)));

  section('Documents (training + joined candidates)');
  line('Status', 'Candidates');
  (data.documents || []).forEach((d) => line(humanize(d.key), d.count));

  section('Joining form (joined candidates)');
  line('Status', 'Candidates');
  (data.joined_forms || []).forEach((d) => line(humanize(d.key), d.count));

  const st = data.stipends || {};
  section(`Stipend / salary payments - ${st.month || ''}`);
  line('Metric', 'Value');
  line('Records', st.records);
  line('Total amount (INR)', st.total_amount);
  line('Government received', st.govt_received);
  line('Government not received', st.govt_not_received);
  line('Paid to candidate', st.candidate_paid);
  line('Not paid to candidate', st.candidate_not_paid);

  section('Support tickets (all, not filtered)');
  line('Status', 'Tickets');
  (data.tickets || []).forEach((t) => line(humanize(t.key), t.count));

  const comm = data.communications || [];
  const commStatuses = [...new Set(comm.flatMap((c) => Object.keys(c.statuses || {})))].sort();
  section('Communications (all, not filtered)');
  line('Channel', 'Total', ...commStatuses.map(humanize));
  comm.forEach((c) => line(humanize(c.channel), c.total, ...commStatuses.map((s) => (c.statuses || {})[s] || 0)));

  section('Latest applications');
  line('Candidate', 'Requirement code', 'Requirement', 'Preferred location', 'Source', 'Status', 'Applied on');
  (data.recent_applications || []).forEach((a) =>
    line(a.candidate_name, a.requirement_code, a.requirement_title, a.preferred_location, humanize(a.source), humanize(a.status), fmtDate(a.applied_at))
  );

  // BOM so Excel opens UTF-8 (₹, Marathi names, etc.) correctly
  return `﻿${rows.join('\r\n')}`;
}

export function downloadCsv(data, filters, opts) {
  const csv = buildCsv(data, filters, opts);
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `JMK-Dashboard-Data_${fileStamp()}.csv`);
}

// ───────────────────────────── PDF ─────────────────────────────

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Capture dashboard views into a landscape A4 PDF.
 * views          — ['statistical'] | ['graphical'] | ['statistical', 'graphical']
 * showView(v)    — switches the page to view v (without saving the preference)
 * getContainer() — returns the element that holds the current view's cards
 * Each card / row is captured separately so nothing is cut across pages.
 */
export async function exportPdf({ views, showView, getContainer, fileLabel, onStep }) {
  const [{ jsPDF }, html2canvasModule] = await Promise.all([import('jspdf'), import('html2canvas')]);
  const html2canvas = html2canvasModule.default || html2canvasModule;

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4', compress: true });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 8;
  const usableW = pageW - margin * 2;
  const usableH = pageH - margin * 2;
  let y = margin;
  let first = true;

  for (const v of views) {
    if (onStep) onStep(v);
    showView(v);
    await wait(900); // let charts measure their width and render

    const container = getContainer();
    if (!container) continue;
    const blocks = [...container.children].filter(
      (el) => el.offsetHeight > 0 && !el.classList.contains('jg-tooltip') && !el.hasAttribute('data-export-skip')
    );

    if (!first) {
      pdf.addPage();
      y = margin;
    }
    first = false;

    for (const el of blocks) {
      // eslint-disable-next-line no-await-in-loop
      const canvas = await html2canvas(el, {
        scale: 2,
        backgroundColor: '#f0f4f3',
        logging: false,
        useCORS: true,
        windowWidth: document.documentElement.scrollWidth,
      });
      let w = usableW;
      let h = (canvas.height * w) / canvas.width;
      if (h > usableH) {
        h = usableH;
        w = (canvas.width * h) / canvas.height;
      }
      if (y + h > pageH - margin) {
        pdf.addPage();
        y = margin;
      }
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', margin + (usableW - w) / 2, y, w, h);
      y += h + 3;
    }
  }

  const total = pdf.getNumberOfPages();
  for (let i = 1; i <= total; i += 1) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(120);
    pdf.text(`JMK Recruitment Dashboard  |  Page ${i} of ${total}`, pageW - margin, pageH - 3, { align: 'right' });
  }

  pdf.save(`JMK-Dashboard-${fileLabel}_${fileStamp()}.pdf`);
}
